import React, { useCallback, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import debounce from 'lodash.debounce'
import { useTranslation } from 'react-i18next'
import { components, components as reactSelectComponents } from 'react-select'

import QualifierDropdown from './QualifierDropdown'
import { useRangeLocations, useRangeLocationsDispatch } from './RangeLocationsContextProvider'
import { actions } from './rangeLocationsReducer'

import targetingApi from 'domain/targeting/api'
import { FdSelect, SearchDropdownIndicator } from 'components/Shared/ReactSelect'
import { SecondaryButton } from 'components/Shared/Button'
import LoadingSpinner, { SpinnerSize } from 'components/Shared/LoadingSpinner'
import { useCampaignSetup } from 'components/Recommendations/Campaign/Context/CampaignSetupContext'
import { derivePlatformSelectionFromSetup } from 'components/Recommendations/Campaign/platform'
import { activeTargetingPlatforms } from '../targeting'
import { providerNamesForPlatforms } from 'domain/platform/permissionErrorTarget'

// eslint-disable-next-line react/display-name
const LocationOption = (t) => (props) => {
  const {
    data: { value },
  } = props

  let leftText = value.name
  if (value.region && value.region !== value.name) {
    leftText += `, ${value.region}`
  }
  if (value.country_name) {
    leftText += `, ${value.country_name}`
  }
  const locationType = (value.geo_hierarchy_name ?? value.type)?.replace('_', ' ')?.toLowerCase()
  const rightText = t(
    `Recommendations.geoTargeting.rangeLocations.locationTypes.${locationType}`,
    locationType,
  )

  return (
    <reactSelectComponents.Option {...props}>
      <div className="range-locations_location-form__option">
        <span className="range-locations_location-form__option-left">{leftText}</span>
        <span className="range-locations_location-form__option-right">{rightText}</span>
      </div>
    </reactSelectComponents.Option>
  )
}

LocationOption.displayName = 'LocationOption'

const NoOptionsMessage = ({ inputValue, searchError, provider, ...props }) => {
  const { t } = useTranslation()

  if (!inputValue) {
    return null
  }

  if (!searchError) {
    return components.NoOptionsMessage(props)
  }

  return (
    <div className="range-locations_location-form__option range-locations_location-form__option--error">
      <div className="range-locations_location-form__option-left error-msg">
        {t('Recommendations.geoTargeting.rangeLocations.searchError', { provider })}
      </div>
      <SecondaryButton
        text={t('common.tryAgain')}
        classNames="range-locations_location-form__option-right"
        color
        // eslint-disable-next-line react/prop-types
        onClick={() => props.selectProps.onInputChange(props.selectProps.inputValue)}
        size="small"
      />
    </div>
  )
}

NoOptionsMessage.propTypes = {
  inputValue: PropTypes.string,
  searchError: PropTypes.bool,
  provider: PropTypes.string,
}

export default function LocationForm({ hasTargetingLimitations = false }) {
  const { t } = useTranslation()
  const [qualifier, setQualifier] = useState('include')
  const [isLoading, setIsLoading] = useState(false)
  const [results, setResults] = useState([])
  const dispatch = useRangeLocationsDispatch()
  const [selectKey, setSelectKey] = useState(0)
  const [searchError, setSearchError] = useState(false)
  const [inputValue, setInputValue] = useState('')
  const { locations } = useRangeLocations()
  const { setup } = useCampaignSetup()
  const activePlatforms = useMemo(
    () => activeTargetingPlatforms(derivePlatformSelectionFromSetup(setup)),
    [setup],
  )
  // The search is issued against whichever platforms the setup targets, so a
  // failure names those rather than always Meta.
  const searchProviderNames = providerNamesForPlatforms(activePlatforms)

  const fetchOptions = async (inputValue) => {
    if (!inputValue) return
    setIsLoading(true)
    setSearchError(false)

    try {
      const response = await targetingApi.searchAsync(inputValue, {
        platforms: activePlatforms,
        locationTypes: hasTargetingLimitations ? ['region', 'country_group', 'city', 'county', 'geo_market', 'address', 'place', 'custom_location', 'medium_geo_area', 'large_geo_area', 'country'] : [],
      })
      if (response.status !== 200 || !response.data.results) {
        setSearchError(true)
      } else {
        const results = response.data.results
          .filter((item) => locations.every((location) => location.data.key !== item.key))
          .map((item) => ({
            value: item,
            label: item.name,
          }))
        setResults(results)
      }
    } catch (error) {
      setSearchError(true)
      console.error('Error fetching data: ', error)
    } finally {
      setIsLoading(false)
    }
  }

  const debouncedFetchOptions = useCallback(debounce(fetchOptions, 500), [locations, activePlatforms])

  const handleInputChange = (inputValue) => {
    setInputValue(inputValue)
    if (inputValue) {
      debouncedFetchOptions(inputValue)
    } else {
      setResults([])
    }
  }

  return (
    <div className="range-locations_location-form">
      <QualifierDropdown
        value={qualifier}
        onChange={(value) => setQualifier(value)}
        hasTargetingLimitations={hasTargetingLimitations}
      />
        
      <FdSelect
        key={selectKey} // reset the select when a location is added
        maxMenuHeight={200}
        isLoading={isLoading}
        options={results}
        onInputChange={handleInputChange}
        placeholder={t('Recommendations.geoTargeting.rangeLocations.searchLocation')}
        noOptionsMessage={() => t('Recommendations.geoTargeting.rangeLocations.noResults')}
        onChange={(option) => {
          setSelectKey(selectKey + 1)
          const location = option.value
          // Default a new location's platform tags to every active platform
          // that actually matched it (platform_results) — a platform absent
          // from platform_results has no id for this location and can't be
          // tagged, matching Figma's default-checked pills that the user can
          // then untoggle per row.
          const platforms = Object.fromEntries(
            activePlatforms.map((platform) => [platform, !!location.platform_results?.[platform]]),
          )
          dispatch({
            type: actions.addLocationAsync,
            payload: { location, qualifier, platforms },
          })
        }}
        openMenuOnFocus={false}
        openMenuOnClick={false}
        components={{
          DropdownIndicator: SearchDropdownIndicator,
          Option: LocationOption(t),
          // eslint-disable-next-line react/display-name
          NoOptionsMessage: (props) => (
            <NoOptionsMessage
              {...props}
              inputValue={inputValue}
              searchError={searchError}
              provider={searchProviderNames}
            />
          ),
          // eslint-disable-next-line react/display-name
          LoadingMessage: () => <LoadingSpinner className="py-3" size={SpinnerSize.SMALL} />,
          LoadingIndicator: () => null,
        }}
        filterOption={() => true} // don't filter options, we want to show all results
      />
    </div>
  )
}

LocationForm.propTypes = {
  hasTargetingLimitations: PropTypes.bool,
}